import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Sparkles,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  User,
  Palette,
  Sun,
  Video,
  Info,
  CreditCard
} from "lucide-react";

const CONVERSION_TYPES = [
  { id: "ai_model_shoot", label: "AI Model Shoot", description: "Garment on realistic AI model" },
  { id: "background_change", label: "Background Enhancement", description: "Professional background replacement" },
  { id: "video_gen", label: "Fashion Video", description: "Short video content" }
];

const OUTPUT_TYPES = [
  { id: "front", label: "Front Model Image", description: "Primary front-facing shot", default: true },
  { id: "back", label: "Back Model Image", description: "Rear view shot", default: true },
  { id: "side", label: "Side Model Image", description: "Profile/side angle", default: true },
  { id: "detail", label: "Detail Close-up", description: "Texture & embroidery focus", default: true }
];

const CreateShoot = () => {
  const navigate = useNavigate();
  const { skuId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Product data
  const [product, setProduct] = useState(null);
  
  // API data
  const [models, setModels] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [lightingStyles, setLightingStyles] = useState([]);
  
  // Selection state
  const [conversionType, setConversionType] = useState("ai_model_shoot");
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [selectedLighting, setSelectedLighting] = useState(null);
  const [outputTypes, setOutputTypes] = useState(["front", "back", "side", "detail"]);
  const [videoRequired, setVideoRequired] = useState(false);
  
  // Filters
  const [modelFilters, setModelFilters] = useState({ gender: "", category: "" });
  const [bgCategory, setBgCategory] = useState("");

  useEffect(() => {
    fetchProduct();
  }, [skuId]);

  useEffect(() => {
    if (product) {
      setModelFilters(prev => ({ ...prev, category: product.category }));
      fetchModels();
      fetchBackgrounds();
      fetchLightingStyles();
    }
  }, [product]);

  useEffect(() => {
    fetchModels();
  }, [modelFilters]);

  useEffect(() => {
    fetchBackgrounds();
  }, [bgCategory]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/skus/${skuId}`, { withCredentials: true });
      setProduct(response.data);
      
      // Check if front image exists
      if (!response.data.image_status?.front) {
        toast.error("Front image is required for AI shoot");
        navigate(`/products/${skuId}`);
        return;
      }
    } catch (error) {
      toast.error("Failed to load product");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const params = new URLSearchParams();
      if (modelFilters.gender) params.append("gender", modelFilters.gender);
      if (modelFilters.category) params.append("category", modelFilters.category);
      
      const response = await axios.get(`${API}/models?${params}`, { withCredentials: true });
      setModels(response.data);
      
      // Auto-select first model if none selected
      if (!selectedModel && response.data.length > 0) {
        setSelectedModel(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to load models");
    }
  };

  const fetchBackgrounds = async () => {
    try {
      const params = bgCategory ? `?category=${bgCategory}` : "";
      const response = await axios.get(`${API}/backgrounds${params}`, { withCredentials: true });
      setBackgrounds(response.data);
      
      // Auto-select first background if none selected
      if (!selectedBackground && response.data.length > 0) {
        setSelectedBackground(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to load backgrounds");
    }
  };

  const fetchLightingStyles = async () => {
    try {
      const response = await axios.get(`${API}/lighting-styles`, { withCredentials: true });
      setLightingStyles(response.data);
    } catch (error) {
      console.error("Failed to load lighting styles");
    }
  };

  const toggleOutputType = (type) => {
    setOutputTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const calculateCredits = () => {
    let credits = 1.0; // Base: 1 SKU = 1 credit
    if (videoRequired) credits += 0.5;
    return credits;
  };

  const canSubmit = () => {
    return selectedModel && selectedBackground && outputTypes.length > 0;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      if (!selectedModel) toast.error("Please select a model");
      else if (!selectedBackground) toast.error("Please select a background");
      else if (outputTypes.length === 0) toast.error("Please select at least one output type");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/shoots/v2`, {
        sku_id: skuId,
        model_id: selectedModel.model_id,
        background_id: selectedBackground.background_id,
        conversion_type: conversionType,
        lighting_style: selectedLighting?.slug || null,
        output_types: outputTypes,
        video_required: videoRequired
      }, { withCredentials: true });

      toast.success("AI shoot started! Processing your images...");
      navigate(`/shoots/${response.data.shoot_id}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create shoot";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  const images = product?.images || {};
  const uploadedImageTypes = Object.keys(images).filter(k => images[k]);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/products/${skuId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Create AI Shoot</h1>
            <p className="text-gray-500">{product?.sku_name} • {product?.sku_code}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Source Images */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Source Images
                </CardTitle>
                <CardDescription>
                  These images will be used for AI generation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-3">
                  {["front", "back", "side", "zoom"].map(type => (
                    <div 
                      key={type}
                      className={`
                        rounded-lg overflow-hidden border-2
                        ${images[type] ? 'border-green-300' : 'border-gray-200'}
                      `}
                    >
                      {images[type] ? (
                        <img 
                          src={images[type].image_url} 
                          alt={type}
                          className="w-full h-20 object-contain bg-white"
                        />
                      ) : (
                        <div className="h-20 flex items-center justify-center bg-gray-50">
                          <span className="text-xs text-gray-400 capitalize">{type}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {uploadedImageTypes.length} of 4 images available
                </p>
              </CardContent>
            </Card>

            {/* Conversion Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Conversion Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {CONVERSION_TYPES.map(type => (
                    <div
                      key={type.id}
                      onClick={() => setConversionType(type.id)}
                      className={`
                        p-4 rounded-lg border-2 cursor-pointer transition-all
                        ${conversionType === type.id 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        {conversionType === type.id && (
                          <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                        )}
                        <span className="font-medium text-sm">{type.label}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Model Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Select AI Model
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-2 mb-4">
                  <select
                    value={modelFilters.gender}
                    onChange={(e) => setModelFilters(prev => ({ ...prev, gender: e.target.value }))}
                    className="px-3 py-1.5 border rounded text-sm"
                  >
                    <option value="">All Genders</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="neutral">Neutral</option>
                  </select>
                </div>
                
                {/* Model Grid */}
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-64 overflow-y-auto">
                  {models.map(model => (
                    <div
                      key={model.model_id}
                      onClick={() => setSelectedModel(model)}
                      className={`
                        p-2 rounded-lg border-2 cursor-pointer transition-all
                        ${selectedModel?.model_id === model.model_id 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'}
                      `}
                    >
                      <img 
                        src={model.preview_image_url}
                        alt={model.name}
                        className="w-full h-20 object-cover rounded mb-1"
                      />
                      <p className="text-xs font-medium truncate">{model.name}</p>
                      <p className="text-xs text-gray-500">{model.complexion}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Background Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Select Background
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Category Filter */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  <Badge 
                    variant={bgCategory === "" ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setBgCategory("")}
                  >
                    All
                  </Badge>
                  {["marketplace", "indoor", "urban", "royal", "nature"].map(cat => (
                    <Badge
                      key={cat}
                      variant={bgCategory === cat ? "default" : "outline"}
                      className="cursor-pointer capitalize"
                      onClick={() => setBgCategory(cat)}
                    >
                      {cat}
                    </Badge>
                  ))}
                </div>
                
                {/* Background Grid */}
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-48 overflow-y-auto">
                  {backgrounds.map(bg => (
                    <div
                      key={bg.background_id}
                      onClick={() => setSelectedBackground(bg)}
                      className={`
                        p-2 rounded-lg border-2 cursor-pointer transition-all
                        ${selectedBackground?.background_id === bg.background_id 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'}
                      `}
                    >
                      <img 
                        src={bg.preview_image_url}
                        alt={bg.name}
                        className="w-full h-16 object-cover rounded mb-1"
                      />
                      <p className="text-xs font-medium truncate">{bg.name}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Lighting Style */}
            {lightingStyles.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sun className="w-5 h-5" />
                    Lighting Style (Optional)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {lightingStyles.map(style => (
                      <div
                        key={style.slug}
                        onClick={() => setSelectedLighting(
                          selectedLighting?.slug === style.slug ? null : style
                        )}
                        className={`
                          p-3 rounded-lg border-2 cursor-pointer transition-all
                          ${selectedLighting?.slug === style.slug 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300'}
                        `}
                      >
                        <p className="text-sm font-medium">{style.name}</p>
                        <p className="text-xs text-gray-500">{style.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Output Config & Summary */}
          <div className="space-y-6">
            {/* Output Types */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Output Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {OUTPUT_TYPES.map(type => (
                    <div key={type.id} className="flex items-start gap-3">
                      <Checkbox
                        id={type.id}
                        checked={outputTypes.includes(type.id)}
                        onCheckedChange={() => toggleOutputType(type.id)}
                      />
                      <div>
                        <Label htmlFor={type.id} className="font-medium">
                          {type.label}
                        </Label>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="video"
                      checked={videoRequired}
                      onCheckedChange={(checked) => setVideoRequired(checked)}
                    />
                    <div>
                      <Label htmlFor="video" className="font-medium flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        Walking Video
                        <Badge variant="outline" className="text-xs">+0.5 credit</Badge>
                      </Label>
                      <p className="text-xs text-gray-500">4-second fashion video</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-indigo-50 border-indigo-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Images ({outputTypes.length})</span>
                  <span>1.0 credit</span>
                </div>
                {videoRequired && (
                  <div className="flex justify-between text-sm">
                    <span>Video</span>
                    <span>+0.5 credit</span>
                  </div>
                )}
                <div className="pt-3 border-t border-indigo-200">
                  <div className="flex justify-between font-bold">
                    <span>Total Credits</span>
                    <span>{calculateCredits()}</span>
                  </div>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit() || submitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="generate-shoot-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate AI Shoot
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-2">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">How it works</p>
                  <ul className="mt-1 space-y-1 text-blue-700">
                    <li>• AI uses all your uploaded images</li>
                    <li>• Generates consistent outputs across views</li>
                    <li>• Typical processing: 2-5 minutes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateShoot;
